import type { ChartMetric as SocialChartMetric, FinanceChartMetric } from '@metapic-apps/shared'

import { normalizeMoney } from '@/lib/adapters/common.adapter'
import type { Trend } from '@/lib/types/common.types'
import type {
  PerformanceChartData,
  PerformanceChartFilter,
  PerformanceChartMetric,
  PerformanceChartPoint,
  PerformanceChartResponse,
  PerformanceMetric,
  PerformanceMetricRaw,
  PerformanceMetrics,
} from '@/lib/types/performance.types'

const CURRENT_SERIES_KEY = 'current'
const PREVIOUS_SERIES_KEY = 'previous'

const SOCIAL_METRICS: SocialChartMetric[] = ['unique_views', 'media_value']
const MONEY_METRICS: PerformanceChartMetric[] = ['media_value', 'order_value']

export const isFinanceMetric = (metric: PerformanceChartMetric): metric is FinanceChartMetric =>
  !(SOCIAL_METRICS as PerformanceChartMetric[]).includes(metric)

export const isMoneyMetric = (metric: PerformanceChartMetric): boolean =>
  MONEY_METRICS.includes(metric)

export const buildChartParams = (filter: PerformanceChartFilter, advertiserId: number) => ({
  advertiser_id: advertiserId,
  date_from: filter.dateFrom,
  date_to: filter.dateTo,
  traffic_source: filter.trafficSource,
  metric: filter.metric,
  ...(filter.creatorId ? { creator_id: filter.creatorId } : {}),
})

// TODO(BE): order_value/media_value should arrive as a money object, but BE currently
// sends a plain number. Once fixed (and the generated types follow), this stays correct —
// just drop the number branch if the chart needs the currency from the response.
const toChartValue = (value: PerformanceChartPoint['value']): number =>
  typeof value === 'number' ? value : normalizeMoney(value).amount

export const toPerformanceChartData = (
  response: PerformanceChartResponse,
): PerformanceChartData => {
  const series = response.series ?? []
  const current = series.find((item) => item.key === CURRENT_SERIES_KEY) ?? series[0]
  const previous = series.find((item) => item.key === PREVIOUS_SERIES_KEY)
  const previousData = previous?.data ?? []

  return {
    labels: current?.data.map((point) => point.date) ?? [],
    current: current?.data.map((point) => toChartValue(point.value)) ?? [],
    previous: previousData.map((point) => toChartValue(point.value)),
    previousLabels: previousData.map((point) => point.date),
    previousRange: previousData.length
      ? { start: previousData[0].date, end: previousData[previousData.length - 1].date }
      : null,
  }
}

const normalizeMetricValue = (value: PerformanceMetricRaw['value']): PerformanceMetric['value'] => {
  if (value === null) {
    return null
  }
  if (typeof value === 'number') {
    return value
  }
  return normalizeMoney(value)
}

const toNumericValue = (value: PerformanceMetricRaw['value']): number | null => {
  if (value === null) {
    return null
  }
  return typeof value === 'number' ? value : Number(value.amount)
}

const computeChangePercent = (
  current: PerformanceMetricRaw['value'],
  previous: PerformanceMetricRaw['value'],
): number | null => {
  const currentValue = toNumericValue(current)
  const previousValue = toNumericValue(previous)
  if (currentValue === null || previousValue === null || previousValue === 0) {
    return null
  }
  return ((currentValue - previousValue) / previousValue) * 100
}

export const normalizePerformanceMetric = (raw: PerformanceMetricRaw): PerformanceMetric => ({
  key: raw.key,
  value: normalizeMetricValue(raw.value),
  changePercent: computeChangePercent(raw.value, raw.previous),
  ...(raw.metadata ? { metadata: raw.metadata } : {}),
})

export const normalizePerformanceMetrics = (rawList: PerformanceMetricRaw[]): PerformanceMetrics =>
  rawList.reduce<PerformanceMetrics>((acc, raw) => {
    acc[raw.key] = normalizePerformanceMetric(raw)
    return acc
  }, {})

export type ChangePercentDisplay = {
  additionalValue?: string
  trend: Trend
}

export const formatChangePercent = (
  changePercent: number | null | undefined,
  lowerIsBetter = false,
): ChangePercentDisplay => {
  if (changePercent === null || changePercent === undefined) {
    return { trend: 'neutral' }
  }
  const magnitude = Math.round(Math.abs(changePercent) * 10) / 10
  if (magnitude === 0) {
    return { additionalValue: '0%', trend: 'neutral' }
  }
  const isIncrease = changePercent > 0
  return {
    additionalValue: `${isIncrease ? '+' : '-'}${magnitude}%`,
    trend: isIncrease !== lowerIsBetter ? 'positive' : 'negative',
  }
}
